// src/Components/dashboard/AdminSettings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import {
  Shield, User, Key, Database, Activity, Clock,
  Bell, Download, RefreshCw, AlertCircle,
  CheckCircle, XCircle, Server, Zap, HardDrive,
  LogOut, Settings, Lock,
} from 'lucide-react';
import { Skeleton, Card, PageHeader, RefreshBtn, ExportBtn } from '../dashboard/design-system';

const StatusBadge = ({ status }) => {
  const map = {
    connected: { cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400', Icon: CheckCircle },
    healthy:   { cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400', Icon: CheckCircle },
    warning:   { cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',        Icon: AlertCircle },
    error:     { cls: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',                Icon: XCircle    },
    unknown:   { cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',              Icon: AlertCircle },
  };
  const { cls, Icon } = map[status] || map.unknown;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>
      <Icon size={11} /> {status || 'Unknown'}
    </span>
  );
};

const Toggle = ({ checked, onChange }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    className={`relative w-10 h-6 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
      checked ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'
    }`}
  >
    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
      checked ? 'translate-x-5' : 'translate-x-1'
    }`} />
  </button>
);

const TABS = [
  { id: 'system',        label: 'System',       Icon: Activity },
  { id: 'account',       label: 'Account',      Icon: User     },
  { id: 'security',      label: 'Security',     Icon: Shield   },
  { id: 'database',      label: 'Database',     Icon: Database },
  { id: 'notifications', label: 'Notifications',Icon: Bell     },
];

const NOTIF_PREFS_KEY = 'beetroot_notif_prefs';
const DEFAULT_PREFS = {
  systemAlerts:       true,
  databaseUpdates:    true,
  performanceReports: false,
  securityAlerts:     true,
};

export default function AdminSettings() {
  const navigate = useNavigate();

  // Read admin info from sessionStorage — set by AdminLogin
  const adminEmail     = sessionStorage.getItem('admin_email') || process.env.REACT_APP_SUPER_ADMIN_EMAIL || '—';
  const loginTime      = sessionStorage.getItem('admin_login_time');
  const sessionExpiry  = sessionStorage.getItem('session_expiry');

  const [activeTab,    setActiveTab]    = useState('system');
  const [loading,      setLoading]      = useState(true);
  const [systemHealth, setSystemHealth] = useState(null);
  const [activityLog,  setActivityLog]  = useState([]);
  const [error,        setError]        = useState(null);
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTIF_PREFS_KEY)) || DEFAULT_PREFS; }
    catch { return DEFAULT_PREFS; }
  });

  const togglePref = (key) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const fetchAll = async () => {
    setLoading(true); setError(null);
    try {
      const [healthRes, logRes] = await Promise.allSettled([
        adminAPI.getSystemHealth(),
        adminAPI.getActivityLog(),
      ]);
      setSystemHealth(
        healthRes.status === 'fulfilled' && healthRes.value.success
          ? healthRes.value.data
          : {
              status: 'unknown',
              timestamp: new Date().toISOString(),
              uptime: { formatted: 'N/A' },
              database: { status: 'unknown', provider: 'Unknown' },
              memory: { heapUsed: 'N/A', heapTotal: 'N/A' },
              environment: 'unknown',
              server: { nodeVersion: 'N/A', platform: 'N/A' },
            }
      );
      if (healthRes.status === 'rejected' || !healthRes.value?.success) {
        setError('Could not reach backend health endpoint.');
      }
      if (logRes.status === 'fulfilled' && logRes.value.success) {
        setActivityLog(logRes.value.data?.activities || []);
      }
    } catch (e) { setError(e.toString()); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Logout — just clear session, no Supabase needed
  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/admin/login');
  };

  const handleExport = () => {
    if (!systemHealth) return;
    const csv = [
      ['Beetroot – System Health', new Date().toLocaleString()],
      [],
      ['Metric', 'Value'],
      ['DB Status',   systemHealth.database?.status   || 'Unknown'],
      ['Provider',    systemHealth.database?.provider || 'Unknown'],
      ['API Status',  systemHealth.status             || 'Unknown'],
      ['Uptime',      systemHealth.uptime?.formatted  || 'N/A'],
      ['Memory',      systemHealth.memory?.heapUsed   || 'N/A'],
      ['Environment', systemHealth.environment        || 'Unknown'],
    ].map(r => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `health-${new Date().toISOString().split('T')[0]}.csv`,
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const formatAgo = ts => {
    if (!ts) return 'Unknown';
    const m = Math.floor((Date.now() - new Date(ts)) / 60000);
    if (m < 1)  return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      <PageHeader title="Admin Settings" sub="System configuration & account management" icon={Settings}>
        <RefreshBtn onClick={fetchAll} loading={loading} />
        <ExportBtn onClick={handleExport} label="Export Report" />
      </PageHeader>

      {/* Admin card */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 text-white border border-white/5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center text-2xl font-black shadow-lg shadow-red-900/30">
              {adminEmail[0]?.toUpperCase() || 'A'}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold">Super Admin</h2>
                <span className="bg-white/10 px-2 py-0.5 rounded-full text-xs flex items-center gap-1 font-medium">
                  <Shield size={10} /> Full Access
                </span>
              </div>
              <p className="text-white/60 text-sm mt-0.5">{adminEmail}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl text-sm font-medium transition">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0" size={18} />
          <div>
            <p className="text-sm text-amber-800 dark:text-amber-300">{error}</p>
            <button onClick={fetchAll} className="text-xs underline mt-1 text-amber-700 dark:text-amber-400">Retry</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Card>
        <div className="border-b border-gray-100 dark:border-gray-800 overflow-x-auto">
          <div className="flex min-w-max px-2">
            {TABS.map(({ id, label, Icon: I }) => (
              <button key={id} onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium transition border-b-2 whitespace-nowrap ${
                  activeTab === id
                    ? 'text-red-600 border-red-600'
                    : 'text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200'
                }`}>
                <I size={15} /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">

          {/* System */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">System Health</h3>
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { Icon: Database,  label: 'Database',    value: systemHealth?.database?.provider || 'Unknown', sub: <StatusBadge status={systemHealth?.database?.status || 'unknown'} /> },
                    { Icon: Server,    label: 'API Status',  value: systemHealth?.status === 'healthy' ? 'Operational' : 'Unknown', sub: <StatusBadge status={systemHealth?.status || 'unknown'} /> },
                    { Icon: Zap,       label: 'Uptime',      value: systemHealth?.uptime?.formatted || 'N/A', sub: 'Process runtime' },
                    { Icon: Activity,  label: 'Node',        value: systemHealth?.server?.nodeVersion || 'N/A', sub: systemHealth?.server?.platform || '' },
                    { Icon: HardDrive, label: 'Memory',      value: systemHealth?.memory?.heapUsed || 'N/A', sub: `Total: ${systemHealth?.memory?.heapTotal || 'N/A'}` },
                    { Icon: Clock,     label: 'Environment', value: systemHealth?.environment || 'Unknown', sub: 'Current mode' },
                  ].map(({ Icon: I, label, value, sub }) => (
                    <div key={label} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-2 mb-2">
                        <I size={14} className="text-gray-400" />
                        <span className="text-xs font-medium text-gray-500">{label}</span>
                      </div>
                      <p className="text-base font-bold text-gray-900 dark:text-white">{value}</p>
                      {typeof sub === 'string' ? <p className="text-xs text-gray-400 mt-0.5">{sub}</p> : <div className="mt-1">{sub}</div>}
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 border border-gray-100 dark:border-gray-800">
                <h4 className="font-semibold text-sm mb-4 flex items-center gap-2 text-gray-800 dark:text-gray-200">
                  <Clock size={14} className="text-gray-400" /> Recent Activity
                </h4>
                {activityLog.length > 0 ? (
                  <div className="space-y-2">
                    {activityLog.slice(0, 6).map((log, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{log.action || 'Activity'}</p>
                            {log.user && <p className="text-xs text-gray-400">By: {log.user}</p>}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{formatAgo(log.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-6">No recent activity</p>
                )}
              </div>
            </div>
          )}

          {/* Account */}
          {activeTab === 'account' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Account</h3>
              {[
                { label: 'Email',         value: adminEmail,             note: '✓ Super admin account'  },
                { label: 'Role',          value: 'Super Administrator',  note: 'Full system access'     },
                { label: 'Session Login', value: loginTime ? new Date(loginTime).toLocaleString() : 'This session', note: null },
                { label: 'Session Expiry',value: sessionExpiry ? new Date(sessionExpiry).toLocaleString() : '8 hours from login', note: null },
              ].map(f => (
                <div key={f.label} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                  <p className="text-xs font-medium text-gray-400 mb-1">{f.label}</p>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{f.value}</p>
                  {f.note && <p className="text-xs text-gray-400 mt-0.5">{f.note}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Security */}
          {activeTab === 'security' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Security</h3>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="text-emerald-600 dark:text-emerald-400" size={18} />
                <div>
                  <p className="font-semibold text-emerald-800 dark:text-emerald-300 text-sm">Account Secure</p>
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">Credentials stored in environment variables. Sessions expire after 8 hours.</p>
                </div>
              </div>
              {[
                { Icon: Key,  label: 'Change Password',           sub: 'Update REACT_APP_SUPER_ADMIN_PASSWORD in .env', badge: null          },
                { Icon: Lock, label: 'Two-Factor Authentication', sub: 'Extra layer of protection',                     badge: 'Coming Soon' },
              ].map(b => (
                <button key={b.label}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-red-400 transition text-left">
                  <div className="flex items-center gap-3">
                    <b.Icon size={18} className="text-gray-500" />
                    <div>
                      <p className="font-medium text-sm text-gray-800 dark:text-gray-200">{b.label}</p>
                      <p className="text-xs text-gray-400">{b.sub}</p>
                    </div>
                  </div>
                  {b.badge
                    ? <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 px-2 py-0.5 rounded-full font-medium">{b.badge}</span>
                    : <span className="text-gray-400">→</span>
                  }
                </button>
              ))}
            </div>
          )}

          {/* Database */}
          {activeTab === 'database' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Database</h3>
              {loading ? (
                <div className="grid grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { label: 'Provider', value: systemHealth?.database?.provider || 'Unknown' },
                    { label: 'Status',   value: <StatusBadge status={systemHealth?.database?.status || 'unknown'} /> },
                    { label: 'API',      value: systemHealth?.status || 'Unknown' },
                    { label: 'Uptime',   value: systemHealth?.uptime?.formatted || 'N/A' },
                  ].map(f => (
                    <div key={f.label} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                      <p className="text-xs font-medium text-gray-400 mb-1">{f.label}</p>
                      {typeof f.value === 'string' ? <p className="font-semibold text-gray-900 dark:text-white text-sm">{f.value}</p> : f.value}
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <button onClick={fetchAll}
                  className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-sky-400 transition">
                  <RefreshCw size={15} className="text-sky-500" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Test Connection</p>
                    <p className="text-xs text-gray-400">Verify DB access</p>
                  </div>
                </button>
                <button onClick={handleExport}
                  className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-emerald-400 transition">
                  <Download size={15} className="text-emerald-500" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Export Report</p>
                    <p className="text-xs text-gray-400">CSV format</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h3>
                <p className="text-sm text-gray-400 mt-0.5">Saved to your browser automatically</p>
              </div>
              {[
                { key: 'systemAlerts',       label: 'System Alerts',       desc: 'Critical errors and warnings' },
                { key: 'databaseUpdates',    label: 'Database Updates',    desc: 'Connection and sync status'   },
                { key: 'performanceReports', label: 'Performance Reports', desc: 'Weekly summaries'             },
                { key: 'securityAlerts',     label: 'Security Alerts',     desc: 'Suspicious access attempts'   },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800">
                  <div>
                    <p className="font-medium text-sm text-gray-800 dark:text-gray-200">{label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                  </div>
                  <Toggle checked={prefs[key]} onChange={() => togglePref(key)} />
                </div>
              ))}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle size={15} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-800 dark:text-emerald-300">
                  Preferences auto-saved and persist across sessions.
                </p>
              </div>
            </div>
          )}

        </div>
      </Card>
    </div>
  );
}